import { datadogRum } from '@datadog/browser-rum'

import { DD_RUM_ENABLED_FLAG } from '@src/config/env'
import { COUNTRY_REGION_IDS, LOCALE_STANDARD_FORMAT } from '@src/config/localisation/locale-config.constants'
import {
  FALLBACK_REGION_ID,
  LOCALE_COOKIE,
  REGION_DETAILS_LOCAL_STORAGE_KEY,
  REGION_ID_COOKIE,
} from '@src/config/localisation/locale-config.static-constants'
import { REGION_ID_HEADER, REGION_LANG_HEADER } from '@src/constants/api.headers.constants'
import { getStoreProgramIdFromStorage } from '@src/utils/b2b/b2b.utils'

import { getIsNativeViewClient } from '../helpers/getIsNativeViewClient'

import { ANALYTIC_COOKIES, USER_CONSENT_COOKIES } from './constants'
import getCookie from './getCookie'
import { getFromLocalStorageWithExpiry } from './getFromLocalStorageWithExpiry'
import isBrowser from './isBrowser'

import type { InternalAxiosRequestConfig } from 'axios'

/**
 * SAND region-header fallback + diagnostic (STRFRNT-16796).
 *
 * SAND requires a request-origin region on `/collection/v3`, `/products/enrich` and `/collection/v5`.
 * The shared `BaseHttpClient` interceptor derives it from cookies via `getLocalisationHeaders`,
 * which is all-or-nothing: it emits NEITHER header unless BOTH `REGION_ID` and
 * `NEXT_PREFERRED_LOCALE` are readable. A cookie-less client therefore sends no region, SAND cannot
 * resolve a release version, and in `prod-eu` that surfaces as a customer-facing HTTP 500
 * (`IllegalStateException: Neither releaseVersion nor live build version found`) — ~0.3–0.4% of EU
 * browser requests, uniformly across desktop/mobile/bot. In the US the same region-less request
 * happens to resolve, so the gap is invisible there.
 *
 * Resolution order, cookie-first so the ~99.6% of requests that already work are untouched:
 *
 *   cookie → server-rendered locale (`__NEXT_DATA__`) → static default
 *
 * The middle tier is what makes this a fix rather than a mute button: it is the locale Next.js
 * negotiated for *this* render, so `/uk` resolves to GB/103 even with an empty cookie jar. Seeding
 * the US default instead would relabel EU traffic as US — the latent defect in the (unmerged) v5
 * client's hardcoded `COLLECTION_V5_DEFAULT_REGION_ID = 101`.
 *
 * Scope notes:
 * - SAND client only. `getLocalisationHeaders` feeds every other service client (cart, checkout,
 *   reviews, BFF) and some of them validate the region they receive — cart-service 422s on a
 *   mismatch — so widening the region's blast radius there is a separate, riskier change.
 * - Client-only. Server-side SAND calls keep their existing behaviour exactly.
 * - Self-contained on purpose: it reads cookies/storage through leaf helpers rather than
 *   `general.utils`, which reaches `HttpClient` via `i18n.utils` and would close an init-order cycle
 *   (HttpClient → this → general.utils → i18n.utils → HttpClient).
 */

/** Which tier supplied the region. `b2b` emits no headers at all, by design. */
export type TSandRegionSource = 'cookie' | 'server_locale' | 'default' | 'b2b'

export interface ISandRegionResolution {
  headers: Record<string, string>
  source: TSandRegionSource
  regionId: number | null
  locale: string | null
}

export const SAND_REGION_FALLBACK_ACTION = 'sand_region_header_fallback'

/** Returns the callback's value, or `null` if it throws — keeps the diagnostic payload total. */
const safe = <T>(read: () => T): T | null => {
  try {
    return read()
  } catch {
    return null
  }
}

const buildHeaders = (regionId: number, locale: string): Record<string, string> => ({
  [REGION_ID_HEADER]: String(regionId),
  [REGION_LANG_HEADER]: locale,
})

/**
 * `language-COUNTRY`, e.g. `en-gb` → `en-GB`. Local rather than `getStandardLocale` from
 * `i18n.utils`, which imports `HttpClient` — see the cycle note above. Null unless two-part.
 */
const toStandardLocale = (locale: string): string | null => {
  const [language, country] = locale.split('-')
  return language && country ? `${language.toLowerCase()}-${country.toUpperCase()}` : null
}

/**
 * The locale Next.js resolved server-side for this render, out of `__NEXT_DATA__`. Cookie-free and
 * present on the first paint. Mirrors `getServerLocaleInMemory` in `report-cart-region-mismatch.ts`.
 */
export const getServerRenderedLocale = (): string | null =>
  safe(() => {
    const nextData = (
      window as unknown as {
        __NEXT_DATA__?: { props?: { pageProps?: { _nextI18Next?: { initialLocale?: string } } } }
      }
    ).__NEXT_DATA__
    return nextData?.props?.pageProps?._nextI18Next?.initialLocale ?? null
  })

/** The REGION_DETAILS localStorage entry, read for diagnostics only — never for resolution. */
const readStoredRegion = (): { regionId?: number | null; locale?: string | null } | null =>
  safe(() => {
    const raw = getFromLocalStorageWithExpiry(REGION_DETAILS_LOCAL_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as { regionId?: number | null; locale?: string | null }) : null
  })

/**
 * Resolves the region/locale headers for a SAND request. Never throws, and only ever returns an
 * empty result for B2B — the last tier is a static default, so a region is always sent.
 */
export const resolveSandRegionHeaders = (): ISandRegionResolution => {
  // B2B suppresses localisation headers entirely (see `getLocalisationHeaders`). Preserved.
  if (safe(() => !!getStoreProgramIdFromStorage())) {
    return { headers: {}, source: 'b2b', regionId: null, locale: null }
  }

  // Tier 1 — cookies. Identical to today's behaviour whenever both are readable.
  const cookieRegionId = safe(() => getCookie(REGION_ID_COOKIE))
  const cookieLocale = safe(() => getCookie(LOCALE_COOKIE))
  const standardCookieLocale = cookieLocale ? toStandardLocale(cookieLocale) : null
  if (cookieRegionId && standardCookieLocale) {
    return {
      headers: buildHeaders(Number(cookieRegionId), standardCookieLocale),
      source: 'cookie',
      regionId: Number(cookieRegionId),
      locale: standardCookieLocale,
    }
  }

  // Tier 2 — the locale this page was server-rendered with. Correct per render, cookie-free.
  const serverLocale = getServerRenderedLocale()
  const serverRegionId = serverLocale ? COUNTRY_REGION_IDS[serverLocale.split('-')[1]?.toLowerCase() as never] : null
  const serverStandardLocale = serverLocale ? toStandardLocale(serverLocale) : null
  if (serverRegionId != null && serverStandardLocale) {
    return {
      headers: buildHeaders(serverRegionId, serverStandardLocale),
      source: 'server_locale',
      regionId: serverRegionId,
      locale: serverStandardLocale,
    }
  }

  // Tier 3 — static default. A wrong-but-present region degrades to a 4xx or mislabelled content
  // rather than the 500 a missing region causes.
  return {
    headers: buildHeaders(FALLBACK_REGION_ID, LOCALE_STANDARD_FORMAT),
    source: 'default',
    regionId: FALLBACK_REGION_ID,
    locale: LOCALE_STANDARD_FORMAT,
  }
}

/**
 * One diagnostic per page view. A CLP fires ~15 SAND calls (grid + per-card enrich) that would each
 * resolve through the same tier with identical context, so without this latch the volume is
 * multiplied by an arbitrary per-page fan-out. Re-arms on path change.
 */
let reportedForPath: string | null = null

/** Test-only: clears the once-per-page latch. */
export const resetSandRegionFallbackReporting = (): void => {
  reportedForPath = null
}

/**
 * Reports a fallback to Datadog RUM.
 *
 * The clients that lose their region cookies are invisible to every existing source: RUM records
 * ZERO 500s on these endpoints where ~16 would be expected at its ~1.6% coverage, and the prod-eu
 * Kong access logs carry no request headers and no real client IP (`client_ip` is the ALB hop). APM
 * shows only that the rate is uniform across device categories and lands on the first collection
 * fetch — which fits BOTH "clients that never keep our cookies" (privacy settings, consent sweeps,
 * or Chrome cross-site prerender, whose documents run JS against an isolated cookie jar) AND "a
 * first-request timing condition", and separates neither. These attributes decide it:
 *   - `was_prerendered` / `activation_start` — direct prerender detector, no log field needed
 *   - `ms_since_page_start` — a race clusters at a few ms; a cookie-less client is uniform
 *   - `cookie_jar_empty` vs `has_lb_user_id_cookie` — whole-jar loss vs only the region pair gone
 *   - `functional_consent` — tests the EU consent-decline hypothesis
 *   - `storage_has_region` — storage survived but cookies did not ⇒ cookie-specific loss
 *
 * RUM-only: internal analytics events are schema-validated against the shared event-schemas
 * package, so a new event name would need a schema change first.
 *
 * Privacy: no cookie values (only presence flags, plus the functional-consent flag already sent as
 * a request header), no full URLs, no query strings — just the path and the referrer's host.
 */
const reportSandRegionFallback = (resolution: ISandRegionResolution, endpoint?: string): void => {
  if (resolution.source === 'cookie' || resolution.source === 'b2b') return
  if (!DD_RUM_ENABLED_FLAG) return

  const path = window.location.pathname
  if (reportedForPath === path) return
  reportedForPath = path

  // eslint-disable-next-line i18next/no-literal-string -- PerformanceEntry type name, not copy
  const nav = safe(() => performance.getEntriesByType('navigation')[0] as { type?: string; activationStart?: number })
  const activationStart = nav?.activationStart ?? 0
  // eslint-disable-next-line @quince/no-direct-cookie -- counting jar size; the utility reads one key
  const cookieCount = safe(() => (document.cookie ? document.cookie.split('; ').length : 0)) ?? 0
  const doc = document as Document & { prerendering?: boolean; wasDiscarded?: boolean }
  const stored = readStoredRegion()

  datadogRum.addAction(SAND_REGION_FALLBACK_ACTION, {
    // Which tier rescued the request, and what it resolved to.
    fallback_source: resolution.source,
    resolved_region_id: resolution.regionId,
    resolved_locale: resolution.locale,
    endpoint: endpoint ?? null,

    // Cookie shape — which half went missing, or whether the jar was empty entirely.
    has_region_id_cookie: !!safe(() => getCookie(REGION_ID_COOKIE)),
    has_locale_cookie: !!safe(() => getCookie(LOCALE_COOKIE)),
    has_lb_user_id_cookie: !!safe(() => getCookie(ANALYTIC_COOKIES.LB_USER_ID)),
    has_lb_user_id_server_cookie: !!safe(() => getCookie(ANALYTIC_COOKIES.LB_USER_ID_SERVER)),
    functional_consent: safe(() => getCookie(USER_CONSENT_COOKIES.FUNCTIONAL)) ?? null,
    cookie_count: cookieCount,
    cookie_jar_empty: cookieCount === 0,
    navigator_cookie_enabled: safe(() => navigator.cookieEnabled),

    // Storage surviving while cookies did not implies a cookie-specific loss, not a blocked client.
    storage_has_region: !!stored?.regionId,
    storage_locale: stored?.locale ?? null,

    // Prerender / activation. `activation_start > 0` means the document was prerendered before the
    // user navigated to it — the leading explanation for an isolated cookie jar.
    is_prerendering: doc.prerendering === true,
    was_prerendered: activationStart > 0,
    activation_start: Math.round(activationStart),
    navigation_type: nav?.type ?? null,
    visibility_state: safe(() => document.visibilityState),
    was_discarded: doc.wasDiscarded === true,

    // Timing — separates a startup race from a client that simply never had cookies.
    ms_since_page_start: safe(() => Math.round(performance.now())),
    document_ready_state: safe(() => document.readyState),

    // Context. Host only, never the full referrer URL.
    page_path: path,
    referrer_host: safe(() => (document.referrer ? new URL(document.referrer).host : null)),
    is_native_view: safe(() => getIsNativeViewClient()),
    server_rendered_locale: getServerRenderedLocale(),
  })
}

/**
 * Single integration point: attaches the resolved region/locale headers to a SAND request and
 * reports whenever a non-cookie tier was needed. No-op on the server and for B2B. Never throws —
 * it must not affect the request it rides on.
 *
 * Headers are set per key because this runs BEFORE `BaseHttpClient`'s own interceptor (axios request
 * interceptors are LIFO), whose `...localisationHeaders` spread is empty in precisely the case being
 * fixed and otherwise carries the identical cookie values.
 */
export const applySandRegionHeaders = (request: InternalAxiosRequestConfig): void => {
  try {
    if (!isBrowser()) return
    const resolution = resolveSandRegionHeaders()
    Object.entries(resolution.headers).forEach(([key, value]) => {
      request.headers[key] = value
    })
    reportSandRegionFallback(resolution, request.url)
  } catch {
    // A diagnostic/fallback must never break the request it observes.
  }
}
